import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../api/client";

// GET /api/playlists 原本在 AdminPage.js、EventFunnelPage.js 各自獨立宣告一次
// useEffect 去抓，且回傳的原始形狀通常已經是呼叫端要的樣子——這裡只收斂
// 這種「掛載時抓一次、不需要手動觸發重新整理」的用法；PlaylistPage.js/
// UserCalendar.js 因為還需要在新增/編輯/刪除群組後手動重抓、或是跟其他
// 有 ignore 旗標保護的 fetch 綁在同一個 effect 裡，沿用各自原本的寫法，
// 沒有勉強套用這個 hook（避免為了套用而改動它們既有的生命週期邏輯）。
export default function usePlaylists() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  // 元件在 fetch 完成前就卸載（例如快速切換頁面）時，避免對已卸載的元件
  // setState 觸發 React 警告。
  //
  // 這裡一定要在 effect 本體裡把它設回 true，不能只靠 useRef(true) 的初始值——
  // React 18+ 的 StrictMode 在開發模式下會刻意把每個元件 mount → unmount（跑一次
  // cleanup）→ remount 一輪，用來檢查 effect 清理邏輯對不對。如果只在 cleanup 裡
  // 設 false、沒有在 effect 本體重新設回 true，模擬的那次 unmount 會把它永久卡在
  // false，之後 refetch() 的 `if (!isMountedRef.current) return;` 就會一路把真正
  // 掛載期間的所有 setState 都當成「元件已卸載」而靜默跳過——網路請求跟後端回應
  // 都正常，但畫面永遠拿不到資料，是實際測試時才抓到的真bug，不是理論上的風險。
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/api/playlists");
      if (!isMountedRef.current) return;
      setPlaylists(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("取得分組列表失敗:", error);
      if (!isMountedRef.current) return;
      setPlaylists([]);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { playlists, loading, refetch };
}
